/**
 * Massager checkin visit API service.
 *
 * All endpoints live under the site-scoped prefix
 * /api/v1/massager/{site_type}/... — the clinic is resolved server-side from
 * the authenticated massager. No clinic_number in the URL (avoids issues with
 * spaces and other URL-unsafe characters).
 */
import { apiClient } from '@/shared/lib/axios';
import { massagerAuthPath } from '@/shared/lib/apiPath';
import type {
  PatientAddressResponse,
  PatientSearchResponse,
  PrecheckSuddenVisitPayload,
  PrecheckSuddenVisitResponse,
  StoreVisitPayload,
  Visit,
  VisitListResponse,
  VisitResponse,
} from '@/types/massager/visit';

/**
 * Build the optional `service_type` query/body slice. Backend accepts the
 * field as nullable and defaults to R2 — we omit it entirely when null so
 * existing R2 callers don't accidentally send `service_type=null`.
 */
function svcParams(serviceType?: string | null): Record<string, string> {
  return serviceType ? { service_type: serviceType } : {};
}

export async function listVisits(
  siteType: string,
  targetDate: string,
  serviceType?: string | null
): Promise<VisitListResponse> {
  const response = await apiClient.get<VisitListResponse>(
    massagerAuthPath(siteType, '/visits'),
    { params: { target_date: targetDate, ...svcParams(serviceType) } }
  );
  return response.data;
}

export async function showVisit(
  siteType: string,
  visitId: number,
  serviceType?: string | null
): Promise<VisitResponse> {
  const response = await apiClient.get<VisitResponse>(
    massagerAuthPath(siteType, `/visits/${visitId}`),
    { params: svcParams(serviceType) }
  );
  return response.data;
}

export async function startVisit(
  siteType: string,
  visitId: number,
  serviceType?: string | null
): Promise<VisitResponse> {
  const response = await apiClient.post<VisitResponse>(
    massagerAuthPath(siteType, `/visits/${visitId}/start`),
    svcParams(serviceType)
  );
  return response.data;
}

export async function doneVisit(
  siteType: string,
  visitId: number,
  serviceType?: string | null
): Promise<VisitResponse> {
  const response = await apiClient.post<VisitResponse>(
    massagerAuthPath(siteType, `/visits/${visitId}/done`),
    svcParams(serviceType)
  );
  return response.data;
}

export async function cancelVisit(
  siteType: string,
  visitId: number,
  serviceType?: string | null
): Promise<VisitResponse> {
  const response = await apiClient.post<VisitResponse>(
    massagerAuthPath(siteType, `/visits/${visitId}/cancel`),
    svcParams(serviceType)
  );
  return response.data;
}

/**
 * Soft-delete a visit (HIT-2329 削除 button). Backend returns
 * `{ success, message }` with `data: null` — no Visit shape on success.
 */
export async function deleteVisit(
  siteType: string,
  visitId: number,
  serviceType?: string | null
): Promise<{ success: boolean; message?: string }> {
  const response = await apiClient.delete<{ success: boolean; message?: string }>(
    massagerAuthPath(siteType, `/visits/${visitId}`),
    { data: svcParams(serviceType) }
  );
  return response.data;
}

export interface UpdateVisitRemarksSuddenInput {
  isSudden: boolean;
  hospitalName: string | null;
  doctorName: string | null;
  suddenReason: string | null;
  coordinationDate: string | null; // YYYY-MM-DD
}

export async function updateVisitRemarks(
  siteType: string,
  visitId: number,
  remarks: string,
  clinicLocationId: number,
  visitDatetimeFrom: string,
  visitMinutes: number,
  isNotHotTreatment: boolean,
  sudden: UpdateVisitRemarksSuddenInput,
  serviceType?: string | null
): Promise<VisitResponse> {
  const response = await apiClient.patch<VisitResponse>(
    massagerAuthPath(siteType, `/visits/${visitId}/remarks`),
    {
      massager_remarks: remarks,
      clinic_location_id: clinicLocationId,
      visit_datetime_from: visitDatetimeFrom,
      visit_minutes: visitMinutes,
      is_not_hot_treatment: isNotHotTreatment,
      is_sudden: sudden.isSudden,
      hospital_name: sudden.isSudden ? sudden.hospitalName : null,
      doctor_name: sudden.isSudden ? sudden.doctorName : null,
      sudden_reason: sudden.isSudden ? sudden.suddenReason : null,
      coordination_date: sudden.isSudden ? sudden.coordinationDate : null,
      ...svcParams(serviceType),
    }
  );
  return response.data;
}

export async function createVisit(
  siteType: string,
  payload: StoreVisitPayload
): Promise<VisitResponse> {
  const response = await apiClient.post<VisitResponse>(
    massagerAuthPath(siteType, '/visits'),
    payload
  );
  return response.data;
}

export async function precheckSuddenVisit(
  siteType: string,
  payload: PrecheckSuddenVisitPayload
): Promise<PrecheckSuddenVisitResponse> {
  const response = await apiClient.post<PrecheckSuddenVisitResponse>(
    massagerAuthPath(siteType, '/visits/precheck-sudden'),
    payload
  );
  return response.data;
}

export async function searchPatients(
  siteType: string,
  name: string,
  targetDate: string
): Promise<PatientSearchResponse> {
  const response = await apiClient.get<PatientSearchResponse>(
    massagerAuthPath(siteType, '/patients/search'),
    { params: { name, target_date: targetDate } }
  );
  return response.data;
}

export async function searchPatientsByGps(
  siteType: string,
  payload: {
    latitude: number;
    longitude: number;
    target_date: string;
    service_type?: 'A2' | 'R2' | null;
  }
): Promise<PatientSearchResponse> {
  const { service_type, ...rest } = payload;
  const response = await apiClient.post<PatientSearchResponse>(
    massagerAuthPath(siteType, '/patients/search-by-gps'),
    {
      ...rest,
      ...(service_type ? { service_type } : {}),
    }
  );
  return response.data;
}

export async function getPatientAddresses(
  siteType: string,
  patientId: number,
  targetDate: string,
  serviceType?: string | null
): Promise<PatientAddressResponse> {
  const response = await apiClient.get<PatientAddressResponse>(
    massagerAuthPath(siteType, `/patients/${patientId}/addresses`),
    {
      params: {
        target_date: targetDate,
        ...(serviceType ? { service_type: serviceType } : {}),
      },
    }
  );
  return response.data;
}

export type { Visit };
